import React, { useMemo } from 'react';
import type { ATGLPropsT } from '@uber/atgl-components';
import { ATGL, COMPONENT_RENDERER_TYPE } from '@uber/atgl-components';
import type { Theme } from 'baseui';
import { useStyletron } from 'baseui';
import { LabelMedium } from 'baseui/typography';
import type { GeneratedContent } from '../types/types';
import { MEDIA_TYPE } from '../types/types';
type GeneratedContentDisplayProps = {
  generatedContent: GeneratedContent[];
  noOfColumnsPerRow: number;
};
const ATGL_ROOT_KEY = 'ROOT';
const ATGL_ITEM_KEY = 'content_item';
const ATGL_ITEM_KEY_PREFIX = 'content_item_';
const ATGL_CARD_GROUP_KEY = 'content_card_group';
const ATGL_CARD_KEY_PREFIX = 'content_card_';
const ATGL_DATA_KEY = 'generated_content_data';
const ATGL_DATA_KEY_PREFIX = 'generated_content_data_';
const AI_RESULT_LABEL = 'AI Generated Result';
const CONTENT_WRAPPER_MIN_HEIGHT = '200px';
const mapMediaTypeToComponentType = (mediaType: MEDIA_TYPE): COMPONENT_RENDERER_TYPE => {
  switch (mediaType) {
    case MEDIA_TYPE.CONTENT_MEDIA_TYPE_IMAGE:
      return COMPONENT_RENDERER_TYPE.IMAGE;
    case MEDIA_TYPE.CONTENT_MEDIA_TYPE_VIDEO:
      return COMPONENT_RENDERER_TYPE.VIDEO;
    case MEDIA_TYPE.CONTENT_MEDIA_TYPE_AUDIO:
      return COMPONENT_RENDERER_TYPE.AUDIO;
    case MEDIA_TYPE.CONTENT_MEDIA_TYPE_TEXT:
      return COMPONENT_RENDERER_TYPE.MARKDOWN;
    case MEDIA_TYPE.CONTENT_MEDIA_TYPE_INVALID:
    default:
      // TODO: throw error and log it once dev testing is done for all types
      return COMPONENT_RENDERER_TYPE.MARKDOWN;
  }
};
const buildComponentConfigMap = (generatedContent: GeneratedContent[], noOfColumnsPerRow: number) => {
  const totalItems = generatedContent.length;
  const cardNodes = generatedContent.map((_, index) => `${ATGL_CARD_KEY_PREFIX}${index + 1}`);
  const componentConfigMap: ATGLPropsT['componentConfigMap'] = {
    [ATGL_ROOT_KEY]: {
      type: COMPONENT_RENDERER_TYPE.ROW,
      nodes: [...(totalItems > 1 ? [ATGL_CARD_GROUP_KEY] : cardNodes)]
    },
    [ATGL_CARD_GROUP_KEY]: {
      type: COMPONENT_RENDERER_TYPE.CARD_GROUP,
      nodes: cardNodes,
      dataKey: ATGL_CARD_GROUP_KEY,
      props: {
        title: `${AI_RESULT_LABEL}s`,
        noOfItemsInRow: noOfColumnsPerRow
      }
    }
  };
  generatedContent.forEach((contentItem, index) => {
    const itemIndex = index + 1;
    const itemKey = `${ATGL_ITEM_KEY_PREFIX}${itemIndex}`;
    const cardKey = `${ATGL_CARD_KEY_PREFIX}${itemIndex}`;
    const componentType = mapMediaTypeToComponentType(contentItem.type);
    componentConfigMap[cardKey] = {
      type: COMPONENT_RENDERER_TYPE.CARD,
      nodes: [itemKey],
      dataKey: cardKey,
      props: {
        title: `Response ${itemIndex}`
      }
    };
    componentConfigMap[itemKey] = {
      type: componentType,
      dataKey: `${ATGL_DATA_KEY_PREFIX}${itemIndex}`
    };
  });
  return componentConfigMap;
};
const buildInputData = (content: string, componentType: COMPONENT_RENDERER_TYPE, dataKey: string = ATGL_DATA_KEY) => {
  switch (componentType) {
    case COMPONENT_RENDERER_TYPE.MARKDOWN:
      return {
        [dataKey]: {
          content
        }
      };
    case COMPONENT_RENDERER_TYPE.IMAGE:
      return {
        [dataKey]: {
          url: content
        }
      };
    case COMPONENT_RENDERER_TYPE.VIDEO:
      return {
        [dataKey]: {
          url: content,
          config: {
            showControls: true
          }
        }
      };
    case COMPONENT_RENDERER_TYPE.AUDIO:
      return {
        [dataKey]: {
          url: content,
          config: {
            showControls: true
          }
        }
      };
    default:
      return {
        [dataKey]: {
          url: content
        }
      };
  }
};
const buildInputDataMap = (generatedContent: GeneratedContent[]) => {
  return generatedContent.reduce<ATGLPropsT['inputData']>((acc, contentItem, index) => {
    const componentType = mapMediaTypeToComponentType(contentItem.type);
    const dataKey = `${ATGL_DATA_KEY_PREFIX}${index + 1}`;
    return {
      ...acc,
      ...buildInputData(contentItem.content, componentType, dataKey)
    };
  }, {});
};
const useGeneratedContentStyles = (theme: Theme) => {
  const containerStyles = useMemo(() => ({
    marginTop: theme.sizing.scale600,
    marginBottom: theme.sizing.scale600,
    padding: theme.sizing.scale600,
    backgroundColor: theme.colors.backgroundSecondary,
    borderRadius: theme.borders.radius300,
    border: `1px solid ${theme.colors.borderOpaque}`
  }), [theme]);
  const headerStyles = useMemo(() => ({
    marginBottom: theme.sizing.scale500,
    paddingBottom: theme.sizing.scale400,
    borderBottom: `1px solid ${theme.colors.borderOpaque}`
  }), [theme]);
  const contentWrapperStyles = useMemo(() => ({
    minHeight: CONTENT_WRAPPER_MIN_HEIGHT,
    width: '100%'
  }), []);
  return {
    containerStyles,
    headerStyles,
    contentWrapperStyles
  };
};
export const GeneratedContentDisplay = ({
  generatedContent,
  noOfColumnsPerRow
}: GeneratedContentDisplayProps) => {
  const [css, theme] = useStyletron();
  const {
    containerStyles,
    headerStyles,
    contentWrapperStyles
  } = useGeneratedContentStyles(theme);
  const atglConfig = useMemo((): ATGLPropsT | null => {
    if (!generatedContent || generatedContent.length === 0) {
      return null;
    }
    return {
      componentConfigMap: buildComponentConfigMap(generatedContent, noOfColumnsPerRow),
      inputData: buildInputDataMap(generatedContent),
      rootKey: ATGL_ROOT_KEY
    };
  }, [generatedContent, noOfColumnsPerRow]);
  if (!atglConfig) {
    return null;
  }
  return <div className={css(containerStyles)}>
      {generatedContent.length === 1 && <div className={css(headerStyles)}>
          <LabelMedium>{AI_RESULT_LABEL}</LabelMedium>
        </div>}
      <div className={css(contentWrapperStyles)}>
        <ATGL {...atglConfig} />
      </div>
    </div>;
};